import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, User, MessageCircle, Send, Ban, Flag } from 'lucide-react';
import { toast } from 'sonner';
import { formatLocalDate } from '../utils/dateUtils';

export const ForumPostDetail = () => {
  const { id } = useParams();
  const { API, token, user } = useAuth();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyContent, setReplyContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPostAndReplies();
  }, [id]);

  const fetchPostAndReplies = async () => {
    try {
      setLoading(true);
      const [postRes, repliesRes] = await Promise.all([
        axios.get(`${API}/forum/posts/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/forum/posts/${id}/replies/filtered`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setPost(postRes.data);
      setReplies(repliesRes.data);
    } catch (error) {
      console.error('Error fetching post:', error);
      toast.error('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockUser = async (userId, userName) => {
    if (!window.confirm(`Block ${userName}? You won't see their posts or replies.`)) return;
    
    try {
      await axios.post(`${API}/users/block/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${userName} has been blocked`);
      fetchPostAndReplies(); // Refresh to hide blocked user's replies
    } catch (error) {
      toast.error('Failed to block user');
    }
  };

  const handleReportContent = async (type, content, authorName) => {
    const reason = window.prompt(`Why are you reporting this ${type} by ${authorName}?\n\nPlease describe the issue:`);
    if (!reason) return;
    
    try {
      await axios.post(`${API}/feedback/report`, {
        issue_type: `Reported ${type}`,
        description: `Reported ${type} by ${authorName}\n\nReason: ${reason}\n\nContent: ${content?.substring(0, 200)}...`,
        page_url: `/forum/${id}`,
        user_email: user?.email,
        user_name: user?.name
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Thank you! Your report has been submitted for review.');
    } catch (error) {
      toast.error('Failed to submit report. Please try again.');
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;

    try {
      setSubmitting(true);
      await axios.post(
        `${API}/forum/posts/${id}/replies`,
        { content: replyContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reply added');
      setReplyContent('');
      fetchPostAndReplies();
    } catch (error) {
      toast.error('Failed to add reply');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-[#A8A29E]">Loading post...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="text-center py-12">
        <p className="text-[#57534E]">Post not found</p>
        <Button onClick={() => navigate('/forum')} className="mt-4">
          Back to Forum
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="forum-post-detail">
      {/* Back Button */}
      <Button
        onClick={() => navigate('/forum')}
        variant="ghost"
        className="text-primary hover:text-primary/80"
        data-testid="back-to-forum-button"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Forum
      </Button>

      {/* Post Card */}
      <Card className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-8">
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-3xl font-nunito font-bold text-[#2D2D2D] leading-tight flex-1">
              {post.title}
            </h1>
            <span className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold">
              {post.category}
            </span>
          </div>

          <div className="flex items-center gap-2 text-[#A8A29E]">
            <User className="w-4 h-4" />
            <span className="font-medium">{post.user_name}</span>
            <span>•</span>
            <span>{formatLocalDate(post.created_at)}</span>
          </div>

          <p className="text-[#57534E] text-lg leading-relaxed whitespace-pre-wrap">
            {post.content}
          </p>

          <div className="flex items-center gap-2 text-primary pt-4 border-t border-stone-100">
            <MessageCircle className="w-5 h-5" />
            <span className="font-semibold">{post.replies_count} {post.replies_count === 1 ? 'Reply' : 'Replies'}</span>
          </div>
        </div>
      </Card>

      {/* Replies */}
      <div className="space-y-4">
        <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D]">Replies</h2>
        {replies.map((reply) => (
          <Card key={reply.id} className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[#A8A29E]">
                  <User className="w-4 h-4" />
                  <span className="font-medium text-[#2D2D2D]">{reply.user_name}</span>
                  <span>•</span>
                  <span>{formatLocalDate(reply.created_at)}</span>
                </div>
                {/* Block User Button - only show for other users */}
                {reply.user_id !== user?.id && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleReportContent('reply', reply.content, reply.user_name)}
                      className="p-1.5 text-stone-400 hover:text-amber-500 hover:bg-amber-50 rounded transition-colors"
                      title="Report this reply"
                      data-testid={`report-reply-${reply.id}`}
                    >
                      <Flag className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleBlockUser(reply.user_id, reply.user_name)}
                      className="p-1.5 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      title={`Block ${reply.user_name}`}
                      data-testid={`block-reply-user-${reply.user_id}`}
                    >
                      <Ban className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <p className="text-[#57534E] leading-relaxed whitespace-pre-wrap">
                {reply.content}
              </p>
            </div>
          </Card>
        ))}
      </div>

      {/* Reply Form */}
      <Card className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6">
        <h3 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-4">Add Your Reply</h3>
        <form onSubmit={handleSubmitReply} className="space-y-4">
          <Textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="Share your thoughts or advice..."
            rows={6}
            required
            className="w-full"
            data-testid="reply-textarea"
          />
          <Button
            type="submit"
            disabled={submitting || !replyContent.trim()}
            className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-3 font-semibold"
            data-testid="submit-reply-button"
          >
            <Send className="w-4 h-4 mr-2" />
            Post Reply
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default ForumPostDetail;
